import { FormLabel, GovFormWeb } from '@gov-design-system-ce/react-form';
import {
	MarkdownRender,
	UseCommunicationDataEmail,
	UseCommunicationDataPhone,
} from '@gov-nx/component/web';
import { useLocale } from '@gov-nx/core/service';
import {
	PageCode,
	useProfilROBReklamaceContextInstance,
} from '@gov-nx/module/page';
import {
	Button,
	commonSelectPropsToSelectProps,
	FormInput,
	FormSelect,
	FormTextarea,
	FormWidthElementLayout,
	LayoutGap,
	Space,
} from '@gov-nx/ui/web';
import { nameFormatting } from '@gov-nx/utils/common';
import { SouhlasCheckbox } from '../../Autorizace/SouhlasCheckbox';
import { AddressSearchForm } from './AddressSearchForm';
import { BirthPlaceChangeForm } from './BirthplaceChangeForm';
import { DeliveryAddressSearchForm } from './DeliveryAddressSearchForm';

const ValueOrEmpty = ({
	label,
	value,
}: {
	label: string;
	value?: string | number;
}) => {
	const { t } = useLocale(PageCode['profil-ROB']);

	return (
		<>
			{label}
			<Space />
			<span className="font-bold">
				{value ? value : t('formular.pole.neuvedeno')}
			</span>
		</>
	);
};

export const ProfileROBComplaintView = ({ formId }: { formId: string }) => {
	const {
		data,
		personCommunicationData,
		controls,
		setControls,
		formDefinition: { form },
		onSubmit,
		resetAddressSearchFields,
		resetDeliveryAddressSearchFields,
		resetBirthPlaceSearchFields,
		isLowAuthorization,
	} = useProfilROBReklamaceContextInstance();
	const code = PageCode['profil-ROB'];
	const isProcessing = controls.processLoading;
	const { t } = useLocale(code);

	if (controls.initialLoading) {
		return null;
	}

	return (
		<GovFormWeb
			formDefinition={form}
			id={formId}
			onSubmit={onSubmit}>
			<LayoutGap gap="l">
				<MarkdownRender>{t('formular.popis')}</MarkdownRender>
				<FormWidthElementLayout
					type="l"
					className="flex flex-col gap-6">
					<LayoutGap gap="xl">
						<FormInput
							{...form.fields.jmeno}
							label={{
								children: (
									<ValueOrEmpty
										label={form.fields.jmeno.label.children}
										value={nameFormatting(data?.jmeno)}
									/>
								),
							}}
						/>
						<FormInput
							{...form.fields.prijmeni}
							label={{
								children: (
									<ValueOrEmpty
										label={form.fields.prijmeni.label.children}
										value={nameFormatting(data?.prijmeni)}
									/>
								),
							}}
						/>
						<FormInput
							{...form.fields.rodnePrijmeni}
							label={{
								children: (
									<ValueOrEmpty
										label={form.fields.rodnePrijmeni.label.children}
										value={nameFormatting(data?.rodnePrijmeni)}
									/>
								),
							}}
						/>
						<FormSelect
							{...commonSelectPropsToSelectProps(
								form.fields.rodinnyStavPartnerstvi
							)}
							label={{
								children: (
									<ValueOrEmpty
										label={form.fields.rodinnyStavPartnerstvi.label.children}
										value={
											data?.rodinnyStavPartnerstvi
												? t(
														'zaznam.rodinny-stav-moznosti.' +
															data?.rodinnyStavPartnerstvi?.replace(
																/\/|\s/g,
																'-'
															)
													)
												: undefined
										}
									/>
								),
							}}
						/>
						<FormSelect
							{...commonSelectPropsToSelectProps(form.fields.pohlavi)}
							label={{
								children: (
									<ValueOrEmpty
										label={form.fields.pohlavi.label.children}
										value={
											data?.pohlavi
												? t('zaznam.pohlavi-moznosti.' + data?.pohlavi)
												: undefined
										}
									/>
								),
							}}
						/>
						<FormInput {...form.fields.datumNarozeni} />

						<LayoutGap gap="s">
							<FormLabel>
								{t('formular.pole.misto-narozeni')}:{' '}
								<span className="font-bold">
									{data?.mistoNarozeni ?? t('formular.pole.neuvedeno')}
								</span>
							</FormLabel>

							{!controls.displayBirthPlace ? (
								<Button
									onClick={() => setControls({ displayBirthPlace: true })}
									isDisabled={isProcessing}
									color="primary"
									size="m"
									type="outlined">
									{t('akce.zmenit-misto')}
								</Button>
							) : (
								<LayoutGap gap="m">
									<BirthPlaceChangeForm />
									<Button
										onClick={() => {
											setControls({ displayBirthPlace: false });
											resetBirthPlaceSearchFields();
										}}
										isDisabled={isProcessing}
										color="primary"
										size="m"
										type="outlined">
										{t('akce.zrusit-zmeny')}
									</Button>
								</LayoutGap>
							)}
						</LayoutGap>

						<LayoutGap gap="s">
							<FormLabel>
								{t('formular.pole.adresa-pobytu')}:{' '}
								<span className="font-bold">
									{data?.adresaPobytu?.adresaText ??
										t('formular.pole.neuvedeno')}
								</span>
							</FormLabel>

							{!controls.displaySearchAddress ? (
								<Button
									onClick={() => setControls({ displaySearchAddress: true })}
									isDisabled={isProcessing}
									color="primary"
									size="m"
									type="outlined">
									{t('akce.zmenit-adresu')}
								</Button>
							) : (
								<LayoutGap gap="m">
									<AddressSearchForm
										code={code}
										isProcessing={isProcessing}
									/>
									<Button
										onClick={() => {
											setControls({ displaySearchAddress: false });
											resetAddressSearchFields();
										}}
										isDisabled={isProcessing}
										color="primary"
										size="m"
										type="outlined">
										{t('akce.zrusit-zmeny')}
									</Button>
								</LayoutGap>
							)}
						</LayoutGap>

						<LayoutGap gap="s">
							<FormLabel>
								{t('formular.pole.dorucovaci-adresa')}:{' '}
								<span className="font-bold">
									{data?.adresaDorucovaci?.adresaText ??
										t('formular.pole.neuvedeno')}
								</span>
							</FormLabel>
							{!controls.displaySearchDeliveryAddress ? (
								<Button
									onClick={() =>
										setControls({ displaySearchDeliveryAddress: true })
									}
									isDisabled={isProcessing}
									color="primary"
									size="m"
									type="outlined">
									{t('akce.zmenit-adresu')}
								</Button>
							) : (
								<LayoutGap gap="m">
									<DeliveryAddressSearchForm
										code={code}
										isProcessing={isProcessing}
									/>
									<Button
										onClick={() => {
											setControls({ displaySearchDeliveryAddress: false });
											resetDeliveryAddressSearchFields();
										}}
										isDisabled={isProcessing}
										color="primary"
										size="m"
										type="outlined">
										{t('akce.zrusit-zmeny')}
									</Button>
								</LayoutGap>
							)}
						</LayoutGap>
						<FormSelect
							{...commonSelectPropsToSelectProps(
								form.fields.omezeniSvepravnosti
							)}
							label={{
								children: (
									<ValueOrEmpty
										label={form.fields.omezeniSvepravnosti.label.children}
										value={
											data?.omezeniSvepravnosti !== undefined
												? data?.omezeniSvepravnosti
													? t('zaznam.omezeni-svepravnosti-status.ano')
													: t('zaznam.omezeni-svepravnosti-status.ne')
												: t('formular.pole.neuvedeno')
										}
									/>
								),
							}}
						/>
						<FormTextarea {...form.fields.popisReklamace} />
					</LayoutGap>
				</FormWidthElementLayout>
				<FormWidthElementLayout
					type="max"
					className="pt-4">
					<LayoutGap gap="m">
						<FormLabel isRequired={true}>
							{t('formular.kontakty.disclaimer')}
						</FormLabel>

						<UseCommunicationDataEmail
							wcagLabels={{
								edit: t('kontaktni-udaje.email.edit.wcag-label'),
								cancel: t('kontaktni-udaje.email.cancel.wca-label'),
							}}
							personCommunicationData={personCommunicationData}
							email={form.formMethods.getValues('email')}
							fields={form.fields}
							onReset={() => form.formMethods.resetField('email')}
						/>
						<UseCommunicationDataPhone
							personCommunicationData={personCommunicationData}
							fields={form.fields}
							phone={form.formMethods.getValues('phone')}
							wcagLabels={{
								edit: t('kontaktni-udaje.phone.edit.wcag-label'),
								cancel: t('kontaktni-udaje.phone.cancel.wca-label'),
							}}
							onReset={() => form.formMethods.resetField('phone')}
						/>
					</LayoutGap>
				</FormWidthElementLayout>
				<SouhlasCheckbox
					show={isLowAuthorization}
					disabled={isProcessing}
				/>
			</LayoutGap>
		</GovFormWeb>
	);
};
